import React, { createContext, useContext, useReducer } from 'react'

// This pattern can be found here: https://kentcdodds.com/blog/how-to-use-react-context-effectively

const defaultState = {
    showUploadModal: false,
    file: null,
    isLoading: false,
    isUploadCompleted: false,
    requiresFilesUpdate: true,
    filesList: []
}

function dropZoneReducer(state, action) {
    switch (action.type) {
        case 'selectFile':
            return {
                ...state,
                file: action.payload,
                showUploadModal: true
            }

        case 'closeModal':
            return {
                ...state,
                file: null,
                showUploadModal: false,
                isLoading: false,
                isUploadCompleted: false,
                requiresFilesUpdate: true
            }

        case 'uploadingFile':
            return {
                ...state,
                isLoading: true
            }

        case 'uploadCompleted':
            return {
                ...state,
                isLoading: false,
                isUploadCompleted: true,
                requiresFilesUpdate: true
            }

        case 'updateFiles':
            return {
                ...state,
                filesList: action.payload,
                requiresFilesUpdate: false
            }

        case 'requestFilesUpdate':
            return {
                ...state,
                requiresFilesUpdate: true
            }

        default:
            throw new Error(`Unhandled action type: ${action.type}`)
    }
}

const DropZoneStateContext = createContext(defaultState)
const DropZoneDispatchContext = createContext(dropZoneReducer)

function DropZoneProvider({ children }) {
    const [ state, dispatch ] = useReducer(dropZoneReducer, defaultState)
    return (
        <DropZoneStateContext.Provider value={state} >
            <DropZoneDispatchContext.Provider value={dispatch} >
                { children }
            </DropZoneDispatchContext.Provider>
        </DropZoneStateContext.Provider>
    )
}

function useDropZoneState() {
    const context = useContext(DropZoneStateContext)

    if (context === undefined) {
        throw new Error('useDropZoneState must be used within a DropZoneProvider')
    }

    return context
}

function useDropZoneDispatch() {
    const dispatch = useContext(DropZoneDispatchContext)

    if (dispatch === undefined) {
        throw new Error('useDropZoneDispatch must be used within a DropZoneProvider')
    }

    // Actions Creators
    const requiresFilesUpdate = () => dispatch({ type: 'requestFilesUpdate' })

    const closeModal = () => dispatch({ type: 'closeModal' })

    const uploadingFile = () => dispatch({ type: 'uploadingFile' })

    const uploadCompleted = () => dispatch({ type: 'uploadCompleted' })

    const selectFile = (file) => dispatch({ type: 'selectFile', payload: file })

    const updateFiles = (filesList) => dispatch({ type: 'updateFiles', payload: filesList  })

    return {
        dispatch,
        requiresFilesUpdate,
        closeModal,
        uploadingFile,
        uploadCompleted,
        selectFile,
        updateFiles
    }
}

export { DropZoneProvider, useDropZoneState, useDropZoneDispatch }
